import { useCallback, useEffect, useState } from 'react';

import type { RecentLocation } from '../components/LocationPicker/types';
import { locationService } from '../services/location.service';

interface UseLocationSearchResult {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: RecentLocation[];
  isSearching: boolean;
  recentLocations: RecentLocation[];
  isLoadingCurrentLocation: boolean;
  getCurrentLocation: () => Promise<RecentLocation | undefined>;
  saveRecentLocation: (location: RecentLocation) => void;
}

export function useLocationSearch(): UseLocationSearchResult {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<RecentLocation[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [recentLocations, setRecentLocations] = useState<RecentLocation[]>([]);
  const [isLoadingCurrentLocation, setIsLoadingCurrentLocation] = useState(false);

  // Load recent locations on mount
  useEffect(() => {
    setRecentLocations(locationService.getRecentLocations());
  }, []);

  // Debounced search effect
  useEffect(() => {
    const searchTimeoutId = setTimeout(async () => {
      if (searchQuery.trim().length >= 2) {
        setIsSearching(true);

        try {
          console.log('[useLocationSearch] Starting search for:', searchQuery);
          const results = await locationService.searchLocations(searchQuery);
          console.log('[useLocationSearch] Search results:', results);
          setSearchResults(results);
        } catch (error) {
          console.error('[useLocationSearch] Search error:', error);
          setSearchResults([]);
        } finally {
          setIsSearching(false);
        }
      } else {
        setSearchResults([]);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(searchTimeoutId);
  }, [searchQuery]);

  const saveRecentLocation = useCallback((location: RecentLocation) => {
    locationService.saveRecentLocation(location);
    setRecentLocations(locationService.getRecentLocations());
  }, []);

  const getCurrentLocation = useCallback(async () => {
    setIsLoadingCurrentLocation(true);
    try {
      const location = await locationService.getCurrentLocation();
      if (location) {
        // Save as recent location
        const recentLocation: RecentLocation = {
          id: location.id,
          name: location.name,
          description: location.description,
          latitude: location.latitude,
          longitude: location.longitude
        };
        saveRecentLocation(recentLocation);
        return recentLocation;
      }
    } catch (error) {
      console.error('Error getting current location:', error);
    } finally {
      setIsLoadingCurrentLocation(false);
    }
  }, [saveRecentLocation]);

  return {
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    recentLocations,
    isLoadingCurrentLocation,
    getCurrentLocation,
    saveRecentLocation
  };
}
